import { useCallback, useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import { useMutation } from 'convex/react';
import { Upload, Image as ImageIcon, Loader2 } from 'lucide-react';

import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { useTelemetry } from '@/hooks/useTelemetry';
import { friendlyAuthError } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { usePublicSettings } from '@/hooks/usePublicSettings';

interface SketchUploaderProps {
  projectId: Id<'projects'>;
  /** Current sketch count from parent — used to enforce the cap client-side. */
  currentCount: number;
  /** Cap is 5 for this release. Passed in so it stays a single source of truth. */
  maxSketches?: number;
}

const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg']);

/**
 * Generate a unique filename for an uploaded lo-fi sketch. Whatever the
 * user named the file (`IMG_001.png`, `Untitled (3).jpg`, etc.) we
 * normalise to `lofi-<timestamp>-<random>.<ext>` so the storage layer
 * never has to deal with duplicate or weird names. The user-visible
 * label is kept separate and still defaults to the original filename.
 */
function uniqueLofiFilename(file: File): string {
  const extFromName = file.name.match(/\.([a-zA-Z0-9]+)$/)?.[1]?.toLowerCase();
  const extFromType = file.type === 'image/png' ? 'png' : file.type === 'image/jpeg' ? 'jpg' : null;
  const ext = extFromName || extFromType || 'png';
  const rand = Math.random().toString(36).slice(2, 10);
  return `lofi-${Date.now()}-${rand}.${ext}`;
}

interface UploadingItem {
  id: string;
  filename: string;
  progress: 'starting' | 'uploading' | 'finalizing' | 'error';
  errorMessage?: string;
}

/**
 * Drop-zone + file picker. Accepts PNG/JPG up to 10MB. Each file
 * goes through:
 *
 *   1. issueUploadUrl   (Convex mutation, owner-scoped)
 *   2. fetch POST       (browser uploads bytes directly to Convex)
 *   3. finalize         (Convex mutation, persists metadata + storageId)
 *
 * Cap enforcement: if currentCount + queued >= maxSketches the picker
 * is disabled. The Convex mutation also enforces the cap server-side.
 *
 * No local label collection here — the user labels each sketch in the
 * SketchList after the upload completes.
 */
export function SketchUploader({
  projectId,
  currentCount,
  maxSketches = 5,
}: SketchUploaderProps) {
  const { token } = useAuth();
  const { toast } = useToast();
  const telemetry = useTelemetry();
  const { maxSketchMb } = usePublicSettings();
  const MAX_BYTES = maxSketchMb * 1024 * 1024;

  const issueUploadUrl = useMutation(api.sketches.issueUploadUrl);
  const finalize = useMutation(api.sketches.finalize);

  const [uploading, setUploading] = useState<UploadingItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const remainingSlots = Math.max(0, maxSketches - currentCount);
  const disabled = !token || remainingSlots === 0;

  const validateFiles = (files: FileList | File[]): { ok: File[]; errors: string[] } => {
    const ok: File[] = [];
    const errors: string[] = [];
    for (const f of Array.from(files)) {
      if (!ALLOWED_TYPES.has(f.type)) {
        errors.push(`${f.name}: only PNG and JPG are accepted.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        errors.push(`${f.name}: exceeds the ${maxSketchMb}MB limit.`);
        continue;
      }
      if (f.size <= 0) {
        errors.push(`${f.name}: empty file.`);
        continue;
      }
      ok.push(f);
    }
    return { ok, errors };
  };

  const upload = useCallback(
    async (files: File[]) => {
      if (!token) return;
      const slots = remainingSlots;
      const accepted = files.slice(0, slots);
      if (files.length > slots) {
        toast({
          variant: 'destructive',
          title: 'Too many sketches',
          description: `Only ${slots} more allowed (cap of ${maxSketches}).`,
        });
      }
      if (accepted.length === 0) return;

      // Default label = filename without extension. The user can rename
      // afterwards via SketchLabelEditor.
      const defaultLabel = (f: File) => f.name.replace(/\.[^.]+$/, '').slice(0, 60) || 'untitled page';

      // Create placeholder rows so the UI shows progress immediately.
      const queued: UploadingItem[] = accepted.map((f) => ({
        id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        filename: f.name,
        progress: 'starting',
      }));
      setUploading((prev) => [...prev, ...queued]);

      const removeQueued = (id: string) =>
        setUploading((prev) => prev.filter((q) => q.id !== id));

      const setQueuedStatus = (id: string, next: Partial<UploadingItem>) =>
        setUploading((prev) => prev.map((q) => (q.id === id ? { ...q, ...next } : q)));

      // Upload sequentially to keep the UI orderly and avoid burst
      // failures on the Convex side.
      for (let i = 0; i < accepted.length; i++) {
        const file = accepted[i];
        const queueId = queued[i].id;
        try {
          // 1. Get an upload URL from Convex.
          setQueuedStatus(queueId, { progress: 'uploading' });
          const uploadUrl = await issueUploadUrl({ token, projectId });

          // 2. POST the bytes directly.
          const result = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file,
          });
          if (!result.ok) {
            const detail = await result.text().catch(() => '');
            throw new Error(`Upload failed (${result.status}): ${detail || result.statusText}`);
          }
          const json = (await result.json()) as { storageId: string };
          const storageId = json.storageId;
          if (!storageId) throw new Error('Convex did not return a storageId');

          // 3. Finalize the sketch row. The stored filename is always a
          // generated unique name; the human label still defaults to the
          // original filename so users can recognise what they uploaded.
          setQueuedStatus(queueId, { progress: 'finalizing' });
          await finalize({
            token,
            projectId,
            storageId: storageId as Id<'_storage'>,
            filename: uniqueLofiFilename(file),
            contentType: file.type,
            bytes: file.size,
            label: defaultLabel(file),
          });

          void telemetry('sketch.uploaded', {
            projectId,
            payload: { bytes: file.size, contentType: file.type },
          });

          // Done — remove from queue.
          removeQueued(queueId);
        } catch (err) {
          setQueuedStatus(queueId, {
            progress: 'error',
            errorMessage: friendlyAuthError(err),
          });
        }
      }
    },
    [token, projectId, remainingSlots, maxSketches, issueUploadUrl, finalize, telemetry, toast],
  );

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const { ok, errors } = validateFiles(files);
    errors.forEach((m) =>
      toast({ variant: 'destructive', title: 'Skipped a file', description: m }),
    );
    if (ok.length > 0) void upload(ok);
    // Reset so the same file can be re-picked later.
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    const { ok, errors } = validateFiles(files);
    errors.forEach((m) =>
      toast({ variant: 'destructive', title: 'Skipped a file', description: m }),
    );
    if (ok.length > 0) void upload(ok);
  };

  return (
    <div className="grid gap-4">
      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        className={cn(
          'relative flex flex-col items-center justify-center gap-3 rounded-sm border-2 border-dashed bg-paper-2/40 p-10 text-center transition-colors',
          disabled
            ? 'cursor-not-allowed opacity-60 border-ink/20'
            : isDragging
              ? 'border-ochre bg-ochre/5'
              : 'border-ink/25 hover:border-ink/40',
        )}
        aria-disabled={disabled}
      >
        <Upload className="h-6 w-6 text-graphite" aria-hidden />
        <div>
          <p className="font-serif text-lg italic text-ink">Drop sketches here.</p>
          <p className="mt-1 text-sm text-graphite">
            or{' '}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
              className="underline decoration-ink/30 underline-offset-4 hover:decoration-ochre hover:text-ochre disabled:no-underline disabled:cursor-not-allowed"
            >
              browse your files
            </button>
          </p>
        </div>
        <p className="font-mono text-[11px] text-graphite">
          PNG or JPG · up to {maxSketchMb}MB · {remainingSlots} of {maxSketches} slots remaining
        </p>

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          multiple
          onChange={onPick}
          className="hidden"
          disabled={disabled}
        />
      </div>

      {/* In-progress queue */}
      {uploading.length > 0 ? (
        <ul className="grid gap-1.5">
          {uploading.map((q) => (
            <li
              key={q.id}
              className={cn(
                'flex items-center gap-3 rounded-sm border px-3 py-2 text-sm',
                q.progress === 'error'
                  ? 'border-vermilion/50 bg-vermilion/5'
                  : 'border-ink/15 bg-paper-2/50',
              )}
            >
              {q.progress === 'error' ? (
                <ImageIcon className="h-4 w-4 text-vermilion" aria-hidden />
              ) : (
                <Loader2 className="h-4 w-4 animate-spin text-ochre" aria-hidden />
              )}
              <span className="flex-1 truncate text-ink">{q.filename}</span>
              <span className="font-mono text-[11px] text-graphite">
                {q.progress === 'error'
                  ? (q.errorMessage ?? 'failed')
                  : q.progress}
              </span>
              {q.progress === 'error' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setUploading((p) => p.filter((x) => x.id !== q.id))}
                  className="h-7 px-2 font-mono text-[11px]"
                >
                  Dismiss
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
